import { useEffect, useState, useCallback, useRef } from "react";
import { Link, useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Checkbox } from "@/components/ui/checkbox";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter, DialogDescription } from "@/components/ui/dialog";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import {
  Trash2, UserPlus, Search, ChevronLeft, ChevronRight, SlidersHorizontal, Download, Upload,
  FileSpreadsheet, Sparkles, Wand2, Bookmark, Plus, X, CheckCircle2, Filter
} from "lucide-react";
import { toast } from "sonner";

const CANONICAL_FIELDS = [
  { key: "", label: "— Skip this column —" },
  { key: "name", label: "Name" },
  { key: "email", label: "Email (required)" },
  { key: "phone", label: "Phone" },
  { key: "business_name", label: "Business name" },
  { key: "business_email", label: "Business email" },
  { key: "company", label: "Company" },
  { key: "job_title", label: "Job title" },
  { key: "website", label: "Website" },
  { key: "address_line1", label: "Address line 1" },
  { key: "address_line2", label: "Address line 2" },
  { key: "city", label: "City" },
  { key: "state", label: "State" },
  { key: "country", label: "Country" },
  { key: "zip_code", label: "Zip / postal code" },
  { key: "tags", label: "Tags (pipe or comma separated)" },
  { key: "notes", label: "Notes" },
];

const EMPTY_FORM = {
  name: "", email: "", phone: "", business_name: "", business_email: "",
  company: "", job_title: "", website: "",
  address_line1: "", address_line2: "", city: "", state: "", country: "", zip_code: "",
  tags: "", notes: "",
};

export default function Contacts() {
  const nav = useNavigate();
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [perPage] = useState(15);
  const [q, setQ] = useState("");
  const [tag, setTag] = useState("");
  const [sort, setSort] = useState("-created_at");
  const [selected, setSelected] = useState(new Set());
  const [open, setOpen] = useState(false);
  const [filtersOpen, setFiltersOpen] = useState(false);
  const [form, setForm] = useState(EMPTY_FORM);
  const [segments, setSegments] = useState([]);
  const [activeSegment, setActiveSegment] = useState(null);
  const [importOpen, setImportOpen] = useState(false);

  const load = useCallback(async () => {
    const { data } = await api.get("/contacts", { params: { q: q || undefined, tag: tag || undefined, sort, page, per_page: perPage } });
    setItems(data.items);
    setTotal(data.total);
    setSelected(new Set());
  }, [q, tag, sort, page, perPage]);

  const loadSegments = useCallback(async () => {
    try { const { data } = await api.get("/segments"); setSegments(data); } catch { /* ignore */ }
  }, []);

  useEffect(() => { load(); }, [load]);
  useEffect(() => { loadSegments(); }, [loadSegments]);

  const allTags = Array.from(new Set(items.flatMap(c => c.tags || [])));

  const create = async () => {
    if (!form.name || !form.email) { toast.error("Name and email required"); return; }
    try {
      await api.post("/contacts", { ...form, tags: form.tags.split(",").map(s => s.trim()).filter(Boolean) });
      setOpen(false);
      setForm(EMPTY_FORM);
      toast.success("Contact added");
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };

  const remove = async (id) => {
    if (!window.confirm("Delete this contact?")) return;
    await api.delete(`/contacts/${id}`);
    toast.success("Contact removed");
    load();
  };

  const bulkDelete = async () => {
    if (selected.size === 0) return;
    if (!window.confirm(`Delete ${selected.size} contact(s)? This cannot be undone.`)) return;
    try {
      const { data } = await api.post("/contacts/bulk-delete", { ids: Array.from(selected) });
      toast.success(`Deleted ${data.deleted} contact${data.deleted === 1 ? "" : "s"}`);
      setSelected(new Set());
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Bulk delete failed"); }
  };

  const exportCsv = () => {
    const cols = ["name", "email", "phone", "business_name", "business_email", "company", "job_title", "website",
      "address_line1", "address_line2", "city", "state", "country", "zip_code", "tags", "notes"];
    const rows = [cols];
    items.forEach(c => rows.push(cols.map(k => Array.isArray(c[k]) ? (c[k] || []).join("|") : (c[k] || ""))));
    const csv = rows.map(r => r.map(v => `"${(v || "").toString().replace(/"/g, '""')}"`).join(",")).join("\n");
    const blob = new Blob([csv], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a"); a.href = url; a.download = "contacts.csv"; a.click();
    URL.revokeObjectURL(url);
  };

  const downloadSample = async () => {
    try {
      const { data } = await api.get("/contacts/sample-csv", { responseType: "blob" });
      const url = URL.createObjectURL(data);
      const a = document.createElement("a"); a.href = url; a.download = "launchdeck-contacts-sample.csv"; a.click();
      URL.revokeObjectURL(url);
    } catch { toast.error("Sample download failed"); }
  };

  const toggleSelect = (id) => {
    const s = new Set(selected);
    if (s.has(id)) s.delete(id); else s.add(id);
    setSelected(s);
  };
  const toggleAll = () => {
    if (selected.size === items.length) setSelected(new Set());
    else setSelected(new Set(items.map(c => c.id)));
  };

  const applySegment = (seg) => {
    setActiveSegment(seg?.id || null);
    const f = seg?.filter || {};
    setQ(f.search || "");
    setTag((f.tags && f.tags[0]) || "");
    setPage(1);
  };

  const removeSegment = async (sid, e) => {
    e.stopPropagation();
    if (!window.confirm("Delete this smart list?")) return;
    await api.delete(`/segments/${sid}`);
    loadSegments();
    if (activeSegment === sid) applySegment(null);
  };

  const totalPages = Math.max(1, Math.ceil(total / perPage));

  const allTagCount = Array.from(new Set(items.flatMap(c => c.tags || []))).length;
  const recentCount = items.filter(c => c.last_activity && new Date(c.last_activity) > new Date(Date.now() - 7 * 24 * 60 * 60 * 1000)).length;

  return (
    <div className="p-6 space-y-6" data-testid="contacts-page">
      {/* Page Header */}
      <div className="rise flex items-start justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-3xl font-bold" style={{ fontFamily: "var(--font-heading)", color: "var(--text)" }}>Contacts</h1>
          <p className="text-sm mt-1" style={{ color: "var(--text-soft)" }}>Your audience — segmented, imported, and ready to convert.</p>
        </div>
        <div className="flex gap-2 flex-wrap">
          <Button variant="outline" onClick={exportCsv} data-testid="contacts-export" className="gap-2"><Download className="w-4 h-4" /> Export CSV</Button>
          <Button data-testid="contacts-import-btn" variant="outline" onClick={() => setImportOpen(true)} className="gap-2"><Upload className="w-4 h-4" /> Smart Import</Button>
          <Dialog open={open} onOpenChange={setOpen}>
            <DialogTrigger asChild>
              <Button data-testid="add-contact-btn" className="text-white gap-2" style={{ background: "var(--brand)" }}><UserPlus className="w-4 h-4" /> Add Contact</Button>
            </DialogTrigger>
            <DialogContent data-testid="add-contact-dialog" className="max-w-3xl max-h-[85vh] overflow-y-auto">
              <DialogHeader>
                <DialogTitle>New Contact</DialogTitle>
                <DialogDescription>All fields optional except name + email.</DialogDescription>
              </DialogHeader>
              <div className="grid grid-cols-2 gap-3">
                <div className="col-span-2"><Label>Name *</Label><Input data-testid="contact-name-input" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} /></div>
                <div><Label>Primary email *</Label><Input data-testid="contact-email-input" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} /></div>
                <div><Label>Business email</Label><Input value={form.business_email} onChange={(e) => setForm({ ...form, business_email: e.target.value })} /></div>
                <div><Label>Phone</Label><Input data-testid="contact-phone-input" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} /></div>
                <div><Label>Website</Label><Input value={form.website} onChange={(e) => setForm({ ...form, website: e.target.value })} placeholder="https://…" /></div>
                <div><Label>Business name</Label><Input data-testid="contact-business-input" value={form.business_name} onChange={(e) => setForm({ ...form, business_name: e.target.value })} /></div>
                <div><Label>Company</Label><Input value={form.company} onChange={(e) => setForm({ ...form, company: e.target.value })} /></div>
                <div className="col-span-2"><Label>Job title</Label><Input value={form.job_title} onChange={(e) => setForm({ ...form, job_title: e.target.value })} /></div>
                <div className="col-span-2 grid grid-cols-2 gap-3 pt-2 border-t" style={{ borderColor: "var(--border)" }}>
                  <div className="col-span-2 text-xs uppercase mono font-semibold" style={{ color: "var(--text-soft)" }}>Address</div>
                  <div className="col-span-2"><Label>Address line 1</Label><Input value={form.address_line1} onChange={(e) => setForm({ ...form, address_line1: e.target.value })} /></div>
                  <div className="col-span-2"><Label>Address line 2</Label><Input value={form.address_line2} onChange={(e) => setForm({ ...form, address_line2: e.target.value })} /></div>
                  <div><Label>City</Label><Input value={form.city} onChange={(e) => setForm({ ...form, city: e.target.value })} /></div>
                  <div><Label>State / Province</Label><Input value={form.state} onChange={(e) => setForm({ ...form, state: e.target.value })} /></div>
                  <div><Label>Country</Label><Input value={form.country} onChange={(e) => setForm({ ...form, country: e.target.value })} /></div>
                  <div><Label>Zip / Postal code</Label><Input value={form.zip_code} onChange={(e) => setForm({ ...form, zip_code: e.target.value })} /></div>
                </div>
                <div className="col-span-2"><Label>Tags (comma separated)</Label><Input data-testid="contact-tags-input" value={form.tags} onChange={(e) => setForm({ ...form, tags: e.target.value })} placeholder="lead, vip, customer" /></div>
                <div className="col-span-2"><Label>Notes</Label><Textarea data-testid="contact-notes-input" value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} rows={3} /></div>
              </div>
              <DialogFooter><Button data-testid="contact-save-btn" onClick={create} className="text-white" style={{ background: "var(--brand)" }}>Add contact</Button></DialogFooter>
            </DialogContent>
          </Dialog>
        </div>
      </div>

      {/* KPI Strip */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        {[
          { label: "Total contacts", value: total, color: "var(--brand)" },
          { label: "Unique tags", value: allTagCount, color: "#8B5CF6" },
          { label: "Active this week", value: recentCount, color: "var(--success)" },
          { label: "Smart lists", value: segments.length, color: "#F59E0B" },
        ].map(({ label, value, color }) => (
          <Card key={label} className="rise card-premium p-4 flex items-center gap-3"
            style={{ background: "var(--surface)", borderColor: "var(--border)" }}>
            <div className="text-2xl font-bold mono" style={{ color, fontFamily: "var(--font-sans)" }}>{value}</div>
            <div className="text-xs font-semibold uppercase tracking-wide" style={{ color: "var(--text-soft)" }}>{label}</div>
          </Card>
        ))}
      </div>

      <Tabs defaultValue="all" onValueChange={(v) => { if (v === "all") applySegment(null); }}>
        <TabsList style={{ background: "var(--surface)", border: "1px solid var(--border)" }}>
          <TabsTrigger value="all" data-testid="contacts-tab-all">All ({total})</TabsTrigger>
          <TabsTrigger value="smart" data-testid="contacts-tab-smart"><Bookmark className="w-3.5 h-3.5 mr-1" /> Smart Lists ({segments.length})</TabsTrigger>
        </TabsList>

        <TabsContent value="smart" className="mt-3">
          <SmartLists
            segments={segments}
            activeId={activeSegment}
            onApply={applySegment}
            onRemove={removeSegment}
            onCreated={loadSegments}
            currentFilter={{ search: q, tags: tag ? [tag] : [] }}
          />
        </TabsContent>
      </Tabs>

      <Card className="card-premium overflow-hidden" style={{ background: "var(--surface)", borderColor: "var(--border)" }}>
        <div className="p-3 border-b flex items-center gap-2 flex-wrap" style={{ borderColor: "var(--border)" }}>
          <div className="relative flex-1 max-w-md">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
            <Input data-testid="contacts-search" value={q} onChange={(e) => { setPage(1); setQ(e.target.value); }} placeholder="Search by name, email, business…" className="pl-9" />
          </div>
          <Button data-testid="contacts-filters-toggle" variant="outline" size="sm" onClick={() => setFiltersOpen(!filtersOpen)} className="gap-1"><SlidersHorizontal className="w-3.5 h-3.5" /> Filters</Button>
          <Select value={sort} onValueChange={(v) => setSort(v)}>
            <SelectTrigger data-testid="contacts-sort" className="w-44 h-9"><SelectValue placeholder="Sort" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="-created_at">Newest first</SelectItem>
              <SelectItem value="created_at">Oldest first</SelectItem>
              <SelectItem value="name">Name A-Z</SelectItem>
              <SelectItem value="-name">Name Z-A</SelectItem>
              <SelectItem value="-last_activity">Recent activity</SelectItem>
            </SelectContent>
          </Select>
          {selected.size > 0 && (
            <div className="ml-auto flex items-center gap-2">
              <span className="text-sm" style={{ color: "var(--text-soft)" }}>{selected.size} selected</span>
              <Button data-testid="contacts-bulk-delete" variant="outline" size="sm" onClick={bulkDelete} className="gap-1 text-red-600 border-red-200 hover:bg-red-50"><Trash2 className="w-3.5 h-3.5" /> Delete selected</Button>
            </div>
          )}
        </div>

        {filtersOpen && (
          <div className="p-3 border-b flex items-center gap-2 flex-wrap" style={{ borderColor: "var(--border)", background: "color-mix(in srgb, var(--brand) 4%, transparent)" }} data-testid="contacts-filters-panel">
            <span className="text-xs uppercase mono font-semibold" style={{ color: "var(--text-soft)" }}>Tags:</span>
            <Badge variant={!tag ? "default" : "outline"} className="cursor-pointer" onClick={() => setTag("")}>All</Badge>
            {allTags.map(t => (
              <Badge key={t} variant={tag === t ? "default" : "outline"} className="cursor-pointer" style={tag === t ? { background: "var(--brand)" } : {}} onClick={() => setTag(t === tag ? "" : t)}>{t}</Badge>
            ))}
          </div>
        )}

        <Table>
          <TableHeader>
            <TableRow style={{ background: "var(--bg)" }}>
              <TableHead className="w-10"><Checkbox data-testid="contacts-toggle-all" checked={selected.size === items.length && items.length > 0} onCheckedChange={toggleAll} /></TableHead>
              <TableHead style={{ color: "var(--text-soft)" }}>Contact</TableHead>
              <TableHead style={{ color: "var(--text-soft)" }}>Phone</TableHead>
              <TableHead style={{ color: "var(--text-soft)" }}>Email</TableHead>
              <TableHead style={{ color: "var(--text-soft)" }}>Business</TableHead>
              <TableHead style={{ color: "var(--text-soft)" }}>Location</TableHead>
              <TableHead style={{ color: "var(--text-soft)" }}>Tags</TableHead>
              <TableHead className="w-12"></TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {items.map((c, i) => {
              const initials = (c.name || c.email).split(" ").map(s => s[0]).join("").slice(0, 2).toUpperCase();
              const loc = [c.city, c.state, c.country].filter(Boolean).join(", ");
              return (
                <TableRow key={c.id} data-testid={`contact-row-${i}`} className="hover:bg-slate-50 dark:hover:bg-slate-800/40">
                  <TableCell><Checkbox data-testid={`contact-check-${i}`} checked={selected.has(c.id)} onCheckedChange={() => toggleSelect(c.id)} onClick={(e) => e.stopPropagation()} /></TableCell>
                  <TableCell className="cursor-pointer" onClick={() => nav(`/contacts/${c.id}`)}>
                    <div className="flex items-center gap-2">
                      <div className="w-7 h-7 rounded-full flex items-center justify-center text-[10px] font-semibold" style={{ background: "color-mix(in srgb, var(--brand) 15%, transparent)", color: "var(--brand)" }}>{initials}</div>
                      <Link to={`/contacts/${c.id}`} className="font-semibold hover:underline" style={{ color: "var(--text)" }}>{c.name}</Link>
                    </div>
                  </TableCell>
                  <TableCell className="mono text-xs" style={{ color: "var(--text-soft)" }}>{c.phone || "—"}</TableCell>
                  <TableCell className="text-sm" style={{ color: "var(--text-soft)" }}>{c.email}</TableCell>
                  <TableCell className="text-sm" style={{ color: "var(--text-soft)" }}>{c.business_name || c.company || "—"}</TableCell>
                  <TableCell className="text-xs" style={{ color: "var(--text-soft)" }}>{loc || "—"}</TableCell>
                  <TableCell>
                    <div className="flex gap-1 flex-wrap">
                      {(c.tags || []).slice(0, 3).map((t) => (
                        <Badge key={t} variant="outline" className="text-xs" style={{ background: "color-mix(in srgb, var(--brand) 10%, transparent)", color: "var(--brand)", borderColor: "color-mix(in srgb, var(--brand) 25%, transparent)" }}>{t}</Badge>
                      ))}
                      {(c.tags || []).length > 3 && <Badge variant="outline" className="text-xs">+{c.tags.length - 3}</Badge>}
                    </div>
                  </TableCell>
                  <TableCell>
                    <Button data-testid={`delete-contact-${i}`} variant="ghost" size="icon" onClick={() => remove(c.id)}><Trash2 className="w-4 h-4 text-slate-400" /></Button>
                  </TableCell>
                </TableRow>
              );
            })}
            {items.length === 0 && (
              <TableRow><TableCell colSpan={8} className="text-center py-12" style={{ color: "var(--text-soft)" }}>
                <Search className="w-8 h-8 mx-auto mb-2 opacity-40" />
                No contacts match. Try importing a CSV or adding one manually.
              </TableCell></TableRow>
            )}
          </TableBody>
        </Table>

        <div className="p-3 border-t flex items-center justify-between" style={{ borderColor: "var(--border)" }}>
          <div className="text-xs mono" style={{ color: "var(--text-soft)" }}>Page {page} of {totalPages}</div>
          <div className="flex gap-1">
            <Button data-testid="contacts-prev" variant="outline" size="sm" disabled={page <= 1} onClick={() => setPage(page - 1)}><ChevronLeft className="w-4 h-4" /></Button>
            <Button data-testid="contacts-next" variant="outline" size="sm" disabled={page >= totalPages} onClick={() => setPage(page + 1)}><ChevronRight className="w-4 h-4" /></Button>
          </div>
        </div>
      </Card>

      <ImportDialog open={importOpen} onOpenChange={setImportOpen} onDone={load} onDownloadSample={downloadSample} />
    </div>
  );
}

// -------------------- Smart Lists panel ---------------------
function SmartLists({ segments, activeId, onApply, onRemove, onCreated, currentFilter }) {
  const [name, setName] = useState("");
  const save = async () => {
    if (!name.trim()) { toast.error("Give your smart list a name"); return; }
    try {
      await api.post("/segments", { name: name.trim(), filter: currentFilter });
      toast.success(`Smart list "${name}" saved`);
      setName("");
      onCreated();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };
  return (
    <Card className="p-4" style={{ background: "var(--surface)", borderColor: "var(--border)" }} data-testid="smartlists-panel">
      <div className="flex flex-wrap gap-2 items-center mb-3">
        <Filter className="w-4 h-4" style={{ color: "var(--brand)" }} />
        <span className="text-sm font-semibold">Save current filters as a Smart List:</span>
        <Input data-testid="segment-name" className="w-56" placeholder="e.g. VIP customers in USA" value={name} onChange={(e) => setName(e.target.value)} />
        <Button data-testid="segment-save" onClick={save} size="sm" className="text-white gap-1" style={{ background: "var(--brand)" }}><Plus className="w-3.5 h-3.5" /> Save list</Button>
      </div>
      <div className="flex flex-wrap gap-2">
        {segments.map((s) => (
          <div key={s.id}
            data-testid={`segment-${s.id}`}
            onClick={() => onApply(s)}
            className={`px-3 py-1.5 rounded-full text-sm cursor-pointer flex items-center gap-2 border transition-all ${activeId === s.id ? "text-white" : ""}`}
            style={activeId === s.id
              ? { background: "var(--brand)", borderColor: "var(--brand)" }
              : { background: "var(--bg)", borderColor: "var(--border)" }}>
            <Bookmark className="w-3.5 h-3.5" />
            {s.name} <span className="text-xs opacity-70">({s.count ?? 0})</span>
            <button onClick={(e) => onRemove(s.id, e)} className="opacity-60 hover:opacity-100"><X className="w-3 h-3" /></button>
          </div>
        ))}
        {segments.length === 0 && (
          <div className="text-sm" style={{ color: "var(--text-soft)" }}>
            No smart lists yet. Apply filters above (search / tags), then save them as a re-usable list.
          </div>
        )}
      </div>
    </Card>
  );
}

// -------------------- Smart Import dialog ---------------------
function ImportDialog({ open, onOpenChange, onDone, onDownloadSample }) {
  const fileRef = useRef(null);
  const [step, setStep] = useState(1); // 1 = pick, 2 = review, 3 = done
  const [file, setFile] = useState(null);
  const [preview, setPreview] = useState(null);
  const [mapping, setMapping] = useState({});
  const [defaultTags, setDefaultTags] = useState("");
  const [updateExisting, setUpdateExisting] = useState(true);
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);
  const [dragOver, setDragOver] = useState(false);

  const reset = () => { setStep(1); setFile(null); setPreview(null); setMapping({}); setResult(null); setDefaultTags(""); };

  useEffect(() => { if (!open) reset(); }, [open]);

  const handleFile = async (f) => {
    if (!f) return;
    if (!/\.(csv|txt)$/i.test(f.name)) { toast.error("Please upload a CSV file"); return; }
    setFile(f);
    setBusy(true);
    try {
      const fd = new FormData(); fd.append("file", f);
      const { data } = await api.post("/contacts/import/preview", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setPreview(data);
      const initial = {};
      data.columns.forEach(c => { if (c.mapped_to) initial[c.index] = c.mapped_to; });
      setMapping(initial);
      setStep(2);
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed to read CSV"); setFile(null); }
    finally { setBusy(false); }
  };

  const onDrop = (e) => {
    e.preventDefault(); setDragOver(false);
    const f = e.dataTransfer.files?.[0]; if (f) handleFile(f);
  };

  const confirm = async () => {
    if (!Object.values(mapping).includes("email")) { toast.error("Map one column to 'Email'"); return; }
    if (!file) return;
    setBusy(true);
    try {
      const buf = await file.arrayBuffer();
      const b64 = btoa(String.fromCharCode(...new Uint8Array(buf)));
      const tags = defaultTags.split(",").map(s => s.trim()).filter(Boolean);
      const { data } = await api.post("/contacts/import", {
        file_b64: b64,
        mapping,
        default_tags: tags,
        update_existing: updateExisting,
      });
      setResult(data);
      setStep(3);
      onDone();
    } catch (e) { toast.error(e?.response?.data?.detail || "Import failed"); }
    finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-3xl max-h-[85vh] overflow-y-auto" data-testid="import-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2"><Wand2 className="w-5 h-5" style={{ color: "var(--brand)" }} /> Smart CSV Import</DialogTitle>
          <DialogDescription>Upload a CSV — columns auto-map by name. Review, then import.</DialogDescription>
        </DialogHeader>

        {step === 1 && (
          <div>
            <div className="flex items-center justify-end mb-3">
              <Button variant="link" size="sm" onClick={onDownloadSample} data-testid="download-sample-btn" className="gap-1"><FileSpreadsheet className="w-3.5 h-3.5" /> Download sample CSV</Button>
            </div>
            <div
              data-testid="import-dropzone"
              onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
              onDragLeave={() => setDragOver(false)}
              onDrop={onDrop}
              onClick={() => fileRef.current?.click()}
              className="border-2 border-dashed rounded-xl p-10 text-center cursor-pointer transition-all"
              style={{ borderColor: dragOver ? "var(--brand)" : "var(--border)", background: dragOver ? "color-mix(in srgb, var(--brand) 8%, transparent)" : "transparent" }}
            >
              <Upload className="w-10 h-10 mx-auto mb-3" style={{ color: "var(--brand)" }} />
              <div className="font-semibold">Drop your CSV here, or click to browse</div>
              <div className="text-sm mt-1" style={{ color: "var(--text-soft)" }}>Any header names — we auto-detect Name, Email, Phone, Address, Zip, and more.</div>
              <input ref={fileRef} type="file" accept=".csv,text/csv" className="hidden" data-testid="import-file" onChange={(e) => handleFile(e.target.files?.[0])} />
            </div>
          </div>
        )}

        {step === 2 && preview && (
          <div>
            <div className="p-3 rounded-lg mb-4 flex items-center justify-between flex-wrap gap-2" style={{ background: "color-mix(in srgb, var(--brand) 8%, transparent)" }}>
              <div className="flex items-center gap-2 text-sm">
                <Sparkles className="w-4 h-4" style={{ color: "var(--brand)" }} />
                <strong>{preview.auto_mapped}</strong> of {preview.columns.length} columns auto-mapped · <strong>{preview.total_rows}</strong> rows detected
              </div>
              {!preview.has_email && <Badge variant="outline" className="border-amber-300 text-amber-700 bg-amber-50">Map an Email column below</Badge>}
            </div>
            <div className="space-y-2 mb-4">
              {preview.columns.map((c) => (
                <div key={c.index} className="grid grid-cols-12 gap-3 items-center p-2 rounded border" style={{ borderColor: "var(--border)" }}>
                  <div className="col-span-4">
                    <div className="text-xs mono uppercase" style={{ color: "var(--text-soft)" }}>Column {c.index + 1}</div>
                    <div className="font-semibold text-sm">{c.header}</div>
                  </div>
                  <div className="col-span-4 text-xs truncate" style={{ color: "var(--text-soft)" }}>
                    e.g. {c.samples.filter(Boolean).slice(0, 2).join(" · ") || "(empty)"}
                  </div>
                  <div className="col-span-4">
                    <Select value={mapping[c.index] || ""} onValueChange={(v) => setMapping({ ...mapping, [c.index]: v })}>
                      <SelectTrigger data-testid={`map-col-${c.index}`}><SelectValue /></SelectTrigger>
                      <SelectContent>
                        {CANONICAL_FIELDS.map(f => <SelectItem key={f.key || "skip"} value={f.key}>{f.label}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </div>
                </div>
              ))}
            </div>
            <div className="grid grid-cols-2 gap-3 mb-3">
              <div>
                <Label>Default tags (applied to all)</Label>
                <Input data-testid="import-default-tags" value={defaultTags} onChange={(e) => setDefaultTags(e.target.value)} placeholder="e.g. imported, march-batch" />
              </div>
              <div className="flex items-end gap-2">
                <Checkbox data-testid="update-existing" checked={updateExisting} onCheckedChange={(v) => setUpdateExisting(!!v)} id="upd-ex" />
                <Label htmlFor="upd-ex" className="cursor-pointer text-sm">Update existing contacts (matched by email)</Label>
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setStep(1)}>Back</Button>
              <Button data-testid="import-confirm" onClick={confirm} disabled={busy} className="text-white gap-2" style={{ background: "var(--brand)" }}>
                <Wand2 className="w-4 h-4" /> {busy ? "Importing…" : `Import ${preview.total_rows} contacts`}
              </Button>
            </DialogFooter>
          </div>
        )}

        {step === 3 && result && (
          <div className="text-center py-6">
            <CheckCircle2 className="w-14 h-14 mx-auto text-emerald-500 mb-3" />
            <div className="display text-2xl mb-2">Import complete!</div>
            <div className="grid grid-cols-3 gap-3 mt-6 max-w-md mx-auto">
              <Card className="p-4"><div className="text-2xl display" style={{ color: "var(--brand)" }}>{result.created}</div><div className="text-xs uppercase mono">Created</div></Card>
              <Card className="p-4"><div className="text-2xl display text-emerald-600">{result.updated}</div><div className="text-xs uppercase mono">Updated</div></Card>
              <Card className="p-4"><div className="text-2xl display text-amber-600">{result.skipped}</div><div className="text-xs uppercase mono">Skipped</div></Card>
            </div>
            {result.errors?.length > 0 && (
              <div className="mt-4 text-left">
                <div className="text-xs uppercase mono mb-1" style={{ color: "var(--text-soft)" }}>First {result.errors.length} errors:</div>
                <div className="text-xs mono max-h-32 overflow-y-auto p-2 rounded" style={{ background: "var(--bg)", color: "var(--text-soft)" }}>
                  {result.errors.map((e, i) => <div key={i}>{e}</div>)}
                </div>
              </div>
            )}
            <DialogFooter className="mt-6">
              <Button variant="outline" onClick={reset}>Import another</Button>
              <Button className="text-white" style={{ background: "var(--brand)" }} onClick={() => onOpenChange(false)} data-testid="import-done">Done</Button>
            </DialogFooter>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
